import React, { Fragment, useRef, useState } from 'react'
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'

// Components
// eslint-disable-next-line import/no-cycle
import { Button } from '../Button'

// Utils
import { joinClassNames } from '../../utils/helpers'

const Dialog = ({ loading, message, onCancel, onConfirm, title, type }) => {
  // State
  const [open, setOpen] = useState(true)

  // Ref
  const cancelButtonRef = useRef(null)

  const configureColor = () => {
    switch (type) {
      case 'success':
        return 'green'
      case 'error':
        return 'red'
      case 'warning':
        return 'orange'
      case 'info':
        return 'blue'
      default:
        return 'blue'
    }
  }

  const renderIcon = () => {
    let icon = null
    const background = `bg-${configureColor()}-100`

    if (type === 'success') {
      icon = <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
    } else if (type === 'error') {
      icon = <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
    } else if (type === 'warning') {
      icon = <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" aria-hidden="true" />
    } else if (type === 'info') {
      icon = <InformationCircleIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
    }

    return (
      <div
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={joinClassNames(
          'mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
          background,
        )}
      >
        {icon}
      </div>
    )
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <HeadlessDialog
        open={open}
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        // Dismissing with Escape or a backdrop click is a cancel, and has to tell the
        // caller so. Every caller mounts this off a piece of state that `onCancel`
        // clears; closing without it left that state set and this stuck shut, so the
        // next thing that needed a confirmation silently got none.
        onClose={() => {
          setOpen(false)
          if (onCancel) onCancel()
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <HeadlessDialog.Panel className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  {renderIcon()}
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <HeadlessDialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {title}
                    </HeadlessDialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{message}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 gap-2 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <Button
                    label="Confirm"
                    loading={loading}
                    onClick={() => {
                      onConfirm()
                      setOpen(false)
                    }}
                  />

                  <Button
                    type="button"
                    label="Cancel"
                    loading={loading}
                    onClick={() => {
                      setOpen(false)
                      if (onCancel) onCancel()
                    }}
                    outlined
                    ref={cancelButtonRef}
                  />
                </div>
              </HeadlessDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HeadlessDialog>
    </Transition.Root>
  )
}

Dialog.propTypes = {
  loading: PropTypes.bool,
  message: PropTypes.string.isRequired,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
}

Dialog.defaultProps = {
  onCancel: null,
  loading: false,
}

export default Dialog
